import React, { useEffect, useLayoutEffect, useCallback, useState, useContext, useRef } from 'react';
import SiteLoader from '../../components/SiteLoader.jsx';
import { AppContext } from '../../context/AppContext.jsx';
import axios from 'axios';
import { useLanguage } from "../../i18n/LanguageContext.jsx";
import i18n from "../../i18n/i18n";
import * as Icon from 'react-bootstrap-icons';
import gsap from 'gsap';
import SplitType from 'split-type';
import ScrollTrigger from '../../../gsap/ScrollTrigger.min.js';
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

gsap.registerPlugin(ScrollTrigger);

const HeroTemplate = ({ itemId, pageTitle }) => {
    const { language } = useLanguage();
    const [page, setPage] = useState(null);
    const [articles, setArticles] = useState(null);
    const [componentLoaded, setComponentLoaded] = useState(false);
    const { siteMap } = useContext(AppContext);

    const pageUrl = `/${language}${process.env.REACT_APP_API_PAGE_URL}`;
    const videoRef = useRef(null);
    const playButtonContainerRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${pageUrl}/${itemId}`);
                setPage(res.data);
            } catch (error) {
                // Gestione errori se necessario
            }
        };
        fetchData();
    }, [pageUrl, itemId]);

    useEffect(() => {
        if (page && siteMap) {
            setComponentLoaded(true);
        } else {
            setComponentLoaded(false);
        }
    }, [page, siteMap]);

    useEffect(() => {
        if (page?.articles) {
            setArticles(page.articles);
        }
    }, [page]);


    // ------------------------------
    //  Fancybox video
    // ------------------------------
    const [isYoutube, setIsYoutube] = useState(false);

    useEffect(() => {
        if (typeof Fancybox === 'undefined') {
            console.warn("Fancybox not loaded");
        }
    }, []);

    useEffect(() => {
        setIsYoutube(page?.friendlyName && (page.friendlyName.toLowerCase().includes("youtube") || page.friendlyName.toLowerCase().includes("youtu.be")));
    }, [page]);

    // Funzione che apre Fancybox solo se isYoutube � true
    const openVideoFancybox = useCallback(() => {
        if (!isYoutube) return;
        Fancybox.show([
            {
                src: page.friendlyName,
                type: "iframe"
            }
        ]);
    }, [isYoutube, page]);
 

    useLayoutEffect(() => {
        if (!componentLoaded) return;

        gsap.fromTo('.hero', 
            {
                filter: 'brightness(1)',
                y: 0,
            },
            {
                scrollTrigger: {
                    trigger: '.hero',
                    start: "center-=200px top",
                    end: "bottom top-=200px",
                    scrub: 1,
                    toggleActions: "play none none reverse",
                    id: "heroBlur",
                    /*markers: true,*/
                },
                filter: 'brightness(0)',
                y: -300,
                ease: 'none',
            }
        );

        return () => {
            ScrollTrigger.getById("heroBlur")?.kill();
        }
    }, [componentLoaded]);

    useLayoutEffect(() => {
        if (!componentLoaded) return;
        const heroContainer = document.querySelector('.hero-container');

        if (!heroContainer || !playButtonContainerRef.current) return;

        const handleMouseEnter = () => {
            gsap.to(playButtonContainerRef.current, {
                opacity: 1,
                duration: 0.3,
                ease: 'power2.out'
            });
        };

        const handleMouseMove = (e) => {
            const rect = playButtonContainerRef.current.getBoundingClientRect();
            const width = rect.width;
            const height = rect.height;

            let x = e.pageX + width / 3;
            let y = e.pageY;

            const heroRect = heroContainer.getBoundingClientRect();
            const minX = heroRect.left;
            const maxX = heroRect.right - width;
            const minY = heroRect.top + window.scrollY;
            const maxY = heroRect.top + window.scrollY + heroRect.height - height;

            x = Math.min(Math.max(x, minX), maxX);
            y = Math.min(Math.max(y, minY), maxY);
            gsap.to(playButtonContainerRef.current, {
                x,
                y,
                duration: 0.7,
                ease: 'power2.out'
            });
        };

        const handleMouseLeave = () => {
            gsap.to(playButtonContainerRef.current, {
                opacity: 0,
                duration: 0.5,
                ease: 'power2.out'
            });
        };

        heroContainer.addEventListener('mouseenter', handleMouseEnter);
        heroContainer.addEventListener('mousemove', handleMouseMove);
        heroContainer.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            heroContainer.removeEventListener('mouseenter', handleMouseEnter);
            heroContainer.removeEventListener('mousemove', handleMouseMove);
            heroContainer.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [componentLoaded]);

    return (
        <div
            className="container-fluid hero hero-container vh-100 bg-black"
            // L'evento onClick viene applicato solo se isYoutube 
            onClick={isYoutube ? openVideoFancybox : undefined}
        >
            <div className="row position-relative">
                {articles?.items &&
                    articles.items[0]?.media &&
                    articles.items[0].media.length > 0 && (
                        <>
                            {articles.items[0].media[0].isVideo ? (
                                <video
                                    ref={videoRef}
                                    className="hero-video col-12 px-0 vh-100 object-fit-cover"
                                    autoPlay
                                    loop
                                    muted
                                >
                                    <source
                                        src={articles.items[0].media[0].url}
                                        type={articles.items[0].media[0].contentType}
                                    />
                                </video>
                            ) : (
                                <img
                                    ref={videoRef}
                                    src={articles.items[0].media[0].url}
                                    className="hero-image col-12 px-0 vh-100 object-fit-cover"
                                    alt="hero media"
                                />
                            )}
                        </>
                    )}
                <div 
                    className={`h-100 ${isYoutube && "cursor-pointer"} hero-overlay left-0 position-absolute top-0 w-100`}
                    style={{
                        background: 'linear-gradient(to right, #000000 0%, #000000a3 40%, #0000003b 70%, #00000000 100%)'
                    }}
                ></div>

                <div className="hero-content col-12 z-1 position-absolute top-50 start-0 translate-middle-y w-100">
                    <div className="container-xl">
                        <div className="row text-white">
                            {articles?.items && articles.items[0]?.description && (
                                <div className="col-12 col-lg-8 col-xl-7">
                                    <h1
                                        className="main-hero-title lh-1 fw-bold m-0 pointer-events-none display-4"
                                        data-animate
                                        dangerouslySetInnerHTML={{ __html: articles.items[0]?.description }}
                                    >
                                    </h1>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Contenitore del pulsante play che segue il cursore: viene renderizzato solo se isYoutube � true */}
                {isYoutube && (
                    <div
                        className="play-button-container d-none d-md-block"
                        ref={playButtonContainerRef}
                        style={{ position: 'absolute', top: 0, left: 0 }}
                    >
                        <button className={`play-button`}>
                            Click to play
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HeroTemplate;
